// Comisiones — Portal Vendedor.
// Mockup: surekeep/mockups/backoffice/portal-vendedor-comisiones.html
//
// Metas (cuánto llevas, tu escalera con las palancas por desbloquear, cómo se compone y tú vs. tú) y Pagos (tu saldo, el
// cronograma de pagos y los cortes anteriores). Sale de `GET /commissions/me` y, en Pagos, de
// `GET /commissions/me/payments`: el vendedor no elige de quién.
import { useState } from 'react';
import { ErrorState, Loading } from '@/kit';
import { ApiError, userMessageOf } from '@/api/errors';
import { cn } from '@/lib/cn';
import { mxDay } from '@/lib/dateHelpers';
import { PageTabs } from '@/shared/components/PageTabs';
import { useScreenTitle } from '@/shared/patterns/AppShell';
import { formatMoneyCents } from '@/shared/lib/format/money';
import {
    factor,
    multiplierNote,
    weekChips,
    weekLabel,
    WeeklyBars,
    whyItems,
    WhyList,
} from '@/domains/retail/admin/comisiones';
import { useMyCommissions } from './commissions/api/useMyCommissions';
import { CARD, TITLE } from './commissions/components/cardStyles';
import { LadderCard } from './commissions/components/LadderCard';
import { MyPaymentsPanel } from './commissions/components/MyPaymentsPanel';
import { ladderOf } from './commissions/lib/ladder';
import { UnlockLevers } from './commissions/components/UnlockLevers';
import {
    barsOf,
    heroOf,
    nextGoalOf,
    unlockLeversOf,
    type HeroPeriod,
} from './commissions/lib/myCommissionsView';

const TABS = [
    { key: 'metas', label: 'Metas' },
    { key: 'pagos', label: 'Pagos' },
] as const;

const PERIODS: ReadonlyArray<{ key: HeroPeriod; label: string }> = [
    { key: 'semana', label: 'Corte en curso' },
    { key: 'mes', label: 'Este mes' },
    { key: 'anio', label: 'Este año' },
];

export function SalesMyCommissions() {
    const [tab, setTab] = useState<'metas' | 'pagos'>('metas');
    const [period, setPeriod] = useState<HeroPeriod>('semana');
    const query = useMyCommissions();
    const data = query.data;
    useScreenTitle(data?.seller.name ? `Comisiones · ${data.seller.name}` : null);

    if (query.isPending) return <Loading size="lg" text="Cargando tus comisiones" />;
    if (!data) {
        // Con un 403 (sin alta de vendedor o sin permiso), reintentar no cambia nada.
        const forbidden = query.error instanceof ApiError && query.error.httpStatus === 403;
        return (
            <ErrorState
                title="No se pudieron cargar tus comisiones"
                description={userMessageOf(query.error)}
                onRetry={
                    forbidden
                        ? undefined
                        : () => {
                              void query.refetch();
                          }
                }
            />
        );
    }

    const week = data.week;
    const hero = heroOf(data, period);
    const goal = nextGoalOf(data.rules, week);
    const levers = unlockLeversOf(data.rules, week);
    const { bars, averageCents, bestCents } = barsOf(data);
    const note = week ? multiplierNote(week) : null;
    const ladder = ladderOf(data.rules, week, data.multiplierCapBps);

    return (
        <div className="flex h-full min-h-0 flex-col">
            <PageTabs
                tabs={TABS}
                active={tab}
                onChange={(key) => {
                    setTab(key === 'pagos' ? 'pagos' : 'metas');
                }}
            />
            {/* Un refetch fallido no tapa lo que ya se leyó. */}
            {query.isError && (
                <p role="status" className="px-3 pt-2 text-[11px] text-muted-foreground">
                    No se pudo actualizar. Lo que ves es la última lectura.
                </p>
            )}
            {tab === 'metas' ? (
                <div className="grid min-h-0 flex-1 gap-3 overflow-auto p-3 lg:grid-cols-[minmax(320px,1fr)_minmax(0,1.7fr)]">
                    <div className="flex flex-col gap-3">
                        <section
                            aria-label="Llevas ganado"
                            className="rounded-xl bg-primary p-5 text-primary-foreground"
                        >
                            <div className="mb-2 flex gap-1" role="group" aria-label="Periodo">
                                {PERIODS.map((p) => (
                                    <button
                                        key={p.key}
                                        type="button"
                                        aria-pressed={p.key === period}
                                        onClick={() => {
                                            setPeriod(p.key);
                                        }}
                                        className={cn(
                                            'rounded-full px-2.5 py-0.5 text-[11px]',
                                            p.key === period
                                                ? 'bg-primary-foreground text-primary'
                                                : 'bg-primary-foreground/15',
                                        )}
                                    >
                                        {p.label}
                                    </button>
                                ))}
                            </div>
                            <p className="text-[11px] font-bold uppercase tracking-[0.08em]">
                                Llevas ganado {hero.label}
                            </p>
                            <p className="text-[40px] font-extrabold leading-tight tabular-nums">
                                {formatMoneyCents(hero.amountCents)}
                            </p>
                            <p className="text-[12px]">
                                {hero.salesCount} {hero.salesCount === 1 ? 'venta' : 'ventas'}
                                {period === 'semana' && week && week.multiplierBps > 10_000
                                    ? ` · multiplicador ${factor(week.multiplierBps)}`
                                    : ''}
                            </p>
                            {/* El motor suma cada corte en el mes y el año de su lunes. */}
                            {period !== 'semana' && (
                                <p className="text-[11px]">
                                    Cuenta los cortes cuya semana empieza {hero.label}.
                                </p>
                            )}
                            {period === 'semana' && week && (
                                <ul
                                    className="mt-2 flex flex-wrap gap-1"
                                    aria-label="Resumen del corte en curso"
                                >
                                    {weekChips(week).map((chip) => (
                                        <li
                                            key={chip}
                                            className="rounded-full bg-primary-foreground/15 px-2 py-0.5 text-[11px]"
                                        >
                                            {chip}
                                        </li>
                                    ))}
                                </ul>
                            )}
                            {goal && (
                                <p className="mt-3 rounded-md bg-primary-foreground/15 px-3 py-2 text-[12px]">
                                    Te faltan {goal.missing} {goal.unit} para +
                                    {formatMoneyCents(goal.rewardCents)} ({goal.ruleName})
                                </p>
                            )}
                        </section>

                        <section className={CARD}>
                            <h2 className={TITLE}>Cómo se compone el corte en curso</h2>
                            {week ? (
                                <>
                                    {note && (
                                        <p className="mb-2 text-[10.5px] text-muted-foreground">
                                            Multiplicador {note}.
                                        </p>
                                    )}
                                    <WhyList items={whyItems(week)} />
                                </>
                            ) : (
                                <p className="text-[11px] text-muted-foreground">
                                    Todavía no tienes ventas en este corte.
                                </p>
                            )}
                        </section>

                        <section className={CARD}>
                            <h2 className={TITLE}>Tú vs. tú · últimas {bars.length} semanas</h2>
                            <WeeklyBars
                                bars={bars}
                                selected={data.period.periodId}
                                label="Tus comisiones por semana"
                            />
                            {/* El corte en curso va a medias: no entra al promedio ni al mejor. */}
                            {averageCents !== null && bestCents !== null && (
                                <p className="mt-2 flex justify-between gap-2 text-[11px]">
                                    <span>
                                        Promedio de cortes anteriores{' '}
                                        <b className="tabular-nums">
                                            {formatMoneyCents(averageCents)}
                                        </b>
                                    </span>
                                    <span>
                                        Mejor corte anterior{' '}
                                        <b className="tabular-nums">
                                            {formatMoneyCents(bestCents)}
                                        </b>
                                    </span>
                                </p>
                            )}
                        </section>
                    </div>

                    <div className="flex flex-col gap-3">
                        <LadderCard ladder={ladder}>
                            <UnlockLevers rows={levers.rows} total={levers.total} />
                        </LadderCard>
                    </div>
                </div>
            ) : (
                <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-auto p-3">
                    <MyPaymentsPanel mine={data} today={mxDay(new Date().toISOString())} />
                    <section className={CARD}>
                        <h2 className={TITLE}>Cortes anteriores · liquidado al cerrar</h2>
                        {data.history.length === 0 ? (
                            <p className="text-[11px] text-muted-foreground">
                                Todavía no tienes cortes anteriores.
                            </p>
                        ) : (
                            <>
                                {/* El motor manda el de la semana pasada aunque no se haya cerrado. */}
                                <p className="mb-1 text-[10.5px] text-muted-foreground">
                                    Si el de la semana pasada aún no se cierra, va calculado al
                                    momento.
                                </p>
                                <ul
                                    className="flex flex-col divide-y divide-border"
                                    aria-label="Cortes anteriores"
                                >
                                    {[...data.history].reverse().map((point) => (
                                        <li
                                            key={point.periodId}
                                            className="flex justify-between py-1.5 text-[12px]"
                                        >
                                            <span>
                                                {weekLabel(point.periodId)} · {point.salesCount}{' '}
                                                {point.salesCount === 1 ? 'venta' : 'ventas'}
                                            </span>
                                            <b className="tabular-nums">
                                                {formatMoneyCents(point.paidCents)}
                                            </b>
                                        </li>
                                    ))}
                                </ul>
                            </>
                        )}
                    </section>
                </div>
            )}
        </div>
    );
}
